import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Edit, Trash2, Eye, EyeOff, Upload, X, ArrowUp, ArrowDown } from 'lucide-react';
import { bannerService, uploadService } from '../services/api';
import toast from 'react-hot-toast';

const BannerManagement = () => {
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingBanner, setEditingBanner] = useState(null);
  const [bannerForm, setBannerForm] = useState({
    title: '',
    image_data: '',
    link_url: '',
    display_order: 0,
    is_active: true
  });
  const [uploadingImage, setUploadingImage] = useState(false);

  useEffect(() => {
    fetchBanners();
  }, []);

  const fetchBanners = async () => {
    try {
      const response = await bannerService.getAll(false);
      setBanners(response.data.sort((a, b) => a.display_order - b.display_order));
    } catch (error) {
      toast.error('Error al cargar banners');
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Solo se permiten imágenes');
      return;
    }

    try {
      setUploadingImage(true);
      const response = await uploadService.uploadFile(file);
      setBannerForm({
        ...bannerForm,
        image_data: response.data.url
      });
      toast.success('Imagen cargada exitosamente');
    } catch (error) {
      toast.error('Error al cargar imagen');
    } finally {
      setUploadingImage(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!bannerForm.image_data) {
      toast.error('Debes cargar una imagen');
      return;
    }

    try {
      if (editingBanner) {
        await bannerService.update(editingBanner.id, bannerForm);
        toast.success('Banner actualizado exitosamente');
      } else {
        await bannerService.create(bannerForm);
        toast.success('Banner creado exitosamente');
      }

      setShowModal(false);
      setEditingBanner(null);
      resetForm();
      fetchBanners();
    } catch (error) {
      toast.error('Error al guardar banner');
    }
  };

  const handleEdit = (banner) => {
    setEditingBanner(banner);
    setBannerForm({
      title: banner.title || '',
      image_data: banner.image_data,
      link_url: banner.link_url || '',
      display_order: banner.display_order,
      is_active: banner.is_active
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Estás seguro de eliminar este banner?')) return;

    try {
      await bannerService.delete(id);
      toast.success('Banner eliminado exitosamente');
      fetchBanners();
    } catch (error) {
      toast.error('Error al eliminar banner');
    }
  };

  const toggleActive = async (banner) => {
    try {
      await bannerService.update(banner.id, {
        ...banner,
        is_active: !banner.is_active
      });
      toast.success(`Banner ${!banner.is_active ? 'activado' : 'desactivado'}`);
      fetchBanners();
    } catch (error) {
      toast.error('Error al actualizar banner');
    }
  };

  const moveOrder = async (banner, direction) => {
    const currentIndex = banners.findIndex(b => b.id === banner.id);
    const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;

    if (newIndex < 0 || newIndex >= banners.length) return;

    const newBanners = [...banners];
    [newBanners[currentIndex], newBanners[newIndex]] = [newBanners[newIndex], newBanners[currentIndex]];

    try {
      await Promise.all(
        newBanners.map((b, index) =>
          bannerService.update(b.id, { ...b, display_order: index })
        )
      );
      fetchBanners();
      toast.success('Orden actualizado');
    } catch (error) {
      toast.error('Error al actualizar orden');
    }
  };

  const resetForm = () => {
    setBannerForm({
      title: '',
      image_data: '',
      link_url: '',
      display_order: banners.length,
      is_active: true
    });
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="loading-spinner w-12 h-12 border-4 border-red-600 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="banner-management">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold text-gray-800">Gestión de Banners</h2>
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => setShowModal(true)}
          className="btn-primary flex items-center space-x-2"
          data-testid="add-banner-btn"
        >
          <Plus className="w-5 h-5" />
          <span>Agregar Banner</span>
        </motion.button>
      </div>

      {/* Banners Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {banners.map((banner, index) => (
          <motion.div
            key={banner.id}
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className={`bg-white rounded-lg shadow-md overflow-hidden border-2 ${
              banner.is_active ? 'border-green-500' : 'border-gray-300'
            }`}
            data-testid={`banner-card-${banner.id}`}
          >
            <div className="relative">
              <img
                src={banner.image_data}
                alt={banner.title || `Banner ${index + 1}`}
                className="w-full h-48 object-cover"
              />
              {!banner.is_active && (
                <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                  <span className="text-white font-bold">DESACTIVADO</span>
                </div>
              )}
            </div>

            <div className="p-4">
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-semibold text-gray-800">{banner.title || 'Sin título'}</h3>
                <span className={`px-2 py-1 text-xs rounded-full ${
                  banner.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'
                }`}>
                  Orden: {banner.display_order}
                </span>
              </div>

              {banner.link_url && (
                <p className="text-sm text-gray-600 mb-3 truncate">{banner.link_url}</p>
              )}

              <div className="flex items-center justify-between space-x-2">
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => moveOrder(banner, 'up')}
                    disabled={index === 0}
                    className="p-1 hover:bg-gray-100 rounded disabled:opacity-30"
                    title="Subir"
                  >
                    <ArrowUp className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => moveOrder(banner, 'down')}
                    disabled={index === banners.length - 1}
                    className="p-1 hover:bg-gray-100 rounded disabled:opacity-30"
                    title="Bajar"
                  >
                    <ArrowDown className="w-4 h-4" />
                  </button>
                </div>

                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => toggleActive(banner)}
                    className="p-2 hover:bg-gray-100 rounded"
                    title={banner.is_active ? 'Desactivar' : 'Activar'}
                  >
                    {banner.is_active ? (
                      <Eye className="w-4 h-4 text-green-600" />
                    ) : (
                      <EyeOff className="w-4 h-4 text-gray-400" />
                    )}
                  </button>
                  <button
                    onClick={() => handleEdit(banner)}
                    className="p-2 hover:bg-blue-50 rounded"
                    title="Editar"
                  >
                    <Edit className="w-4 h-4 text-blue-600" />
                  </button>
                  <button
                    onClick={() => handleDelete(banner.id)}
                    className="p-2 hover:bg-red-50 rounded"
                    title="Eliminar"
                  >
                    <Trash2 className="w-4 h-4 text-red-600" />
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {banners.length === 0 && (
        <div className="text-center py-12 bg-white rounded-lg border-2 border-dashed border-gray-300">
          <p className="text-gray-500">No hay banners creados. ¡Agrega el primero!</p>
        </div>
      )}

      {/* Modal */}
      <AnimatePresence>
        {showModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => {
              setShowModal(false);
              setEditingBanner(null);
              resetForm();
            }}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
            >
              <div className="sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-center">
                <h3 className="text-xl font-semibold">
                  {editingBanner ? 'Editar Banner' : 'Agregar Banner'}
                </h3>
                <button
                  onClick={() => {
                    setShowModal(false);
                    setEditingBanner(null);
                    resetForm();
                  }}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="p-6 space-y-4">
                {/* Título */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Título (opcional)
                  </label>
                  <input
                    type="text"
                    value={bannerForm.title}
                    onChange={(e) => setBannerForm({ ...bannerForm, title: e.target.value })}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-red-500"
                    placeholder="Título del banner"
                  />
                </div>

                {/* Imagen */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Imagen del Banner *
                  </label>
                  {bannerForm.image_data ? (
                    <div className="relative">
                      <img
                        src={bannerForm.image_data}
                        alt="Preview"
                        className="w-full h-64 object-cover rounded-lg"
                      />
                      <button
                        type="button"
                        onClick={() => setBannerForm({ ...bannerForm, image_data: '' })}
                        className="absolute top-2 right-2 bg-red-600 text-white p-2 rounded-full"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ) : (
                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-6">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleImageUpload}
                        className="hidden"
                        id="banner-image"
                        disabled={uploadingImage}
                      />
                      <label
                        htmlFor="banner-image"
                        className="cursor-pointer flex flex-col items-center"
                      >
                        <Upload className="w-12 h-12 text-gray-400 mb-2" />
                        <span className="text-sm text-gray-600">
                          {uploadingImage ? 'Subiendo...' : 'Click para subir imagen'}
                        </span>
                        <span className="text-xs text-gray-500 mt-1">
                          Recomendado: 1920x500px
                        </span>
                      </label>
                    </div>
                  )}
                </div>

                {/* Link URL */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    URL de enlace (opcional)
                  </label>
                  <input
                    type="text"
                    value={bannerForm.link_url}
                    onChange={(e) => setBannerForm({ ...bannerForm, link_url: e.target.value })}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-red-500"
                    placeholder="https://ejemplo.com"
                  />
                </div>

                {/* Activo */}
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="is_active"
                    checked={bannerForm.is_active}
                    onChange={(e) => setBannerForm({ ...bannerForm, is_active: e.target.checked })}
                    className="w-4 h-4 text-red-600 rounded"
                  />
                  <label htmlFor="is_active" className="text-sm font-medium text-gray-700">
                    Banner activo (visible en la página)
                  </label>
                </div>

                {/* Botones */}
                <div className="flex justify-end space-x-4 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowModal(false);
                      setEditingBanner(null);
                      resetForm();
                    }}
                    className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                    disabled={!bannerForm.image_data || uploadingImage}
                  >
                    {editingBanner ? 'Actualizar' : 'Crear'} Banner
                  </button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default BannerManagement;
